/**
 * Single source of truth for all assets. Both editions (projection + mobile) load
 * exactly this list from their `assets/` folder (copied from the repository root
 * `assets/` at build time).
 *
 * Sizes are in world units (visible height = 1000 WU). Vector files (SVG) are
 * rasterised at the size they are displayed with on the current device, so they
 * stay sharp on 4K projectors and on phones alike. Bitmap files (PNG/WebP) are
 * used as delivered – export them for the largest target (1080p/4K).
 */
export const TextureKey = {
  /** the fox per age group (see `TUNING.life.looks`), spritesheets (frames: PLAYER_FRAME in objects/Bird.ts) */
  FoxYoung: 'fox-young',
  FoxOld: 'fox-old',
  /** obstacle standing on the ground (open end at the top) … */
  Pipe: 'pipe',
  /** … and hanging from the top (end at the bottom) */
  PipeTop: 'pipe-top',
  Sky: 'sky',
  /** city skyline per location (see `skylineFor`) */
  Skyline: 'skyline',
  SkylineBerlin: 'skyline-berlin',
  Ground: 'ground',
  /** gold coin = own contribution (front) … */
  Coin: 'coin',
  /** … turning: front, 3× turned (edge on the left), edge (see `COIN_SPIN_FRAMES` in objects/CoinField.ts) */
  CoinSpin: 'coin-spin',
  /** bonus coin = Grundzulage, flies to the figure by itself */
  CoinBonus: 'coin-bonus',
  CoinBonusSpin: 'coin-bonus-spin',
  /** special coins (shared/scoring.ts, SPECIAL_COINS): front with their symbol, they do not turn */
  CoinStarter: 'coin-starter',
  CoinBaby: 'coin-baby',
  /** pulls the banner at the subsidy checkpoints */
  Plane: 'plane',
  /** drifts by in the background (objects/Clouds.ts) */
  Cloud: 'cloud',
} as const;
export type TextureKey = (typeof TextureKey)[keyof typeof TextureKey];

interface BaseAsset {
  key: TextureKey;
  /** path relative to the edition's `assets/` folder */
  file: string;
}

export interface SvgAsset extends BaseAsset {
  type: 'svg';
  /** raster size in world units (of the whole file) */
  width: number;
  height: number;
  /** spritesheet: number of equally wide frames side by side (frames are named 0, 1, …) */
  frames?: number;
}

export interface ImageAsset extends BaseAsset {
  type: 'image';
  /** spritesheet: number of equally wide frames side by side (frames are named 0, 1, …) */
  frames?: number;
}

export type AssetEntry = SvgAsset | ImageAsset;

export const ASSETS: readonly AssetEntry[] = [
  // bitmaps from the pixel-art poses in art/fox (scripts/build-fox-sprites.py), frames of 480×432
  { key: TextureKey.FoxYoung, type: 'image', file: 'images/fox-young.png', frames: 7 },
  { key: TextureKey.FoxOld, type: 'image', file: 'images/fox-old.png', frames: 7 },
  { key: TextureKey.Pipe, type: 'svg', file: 'images/pipe.svg', width: 48, height: 1000 },
  { key: TextureKey.PipeTop, type: 'svg', file: 'images/pipe-top.svg', width: 48, height: 1000 },
  { key: TextureKey.Sky, type: 'svg', file: 'images/sky.svg', width: 1000, height: 1000 },
  { key: TextureKey.Skyline, type: 'svg', file: 'images/skyline.svg', width: 1600, height: 300 },
  { key: TextureKey.SkylineBerlin, type: 'svg', file: 'images/skyline-berlin.svg', width: 1575, height: 300 },
  { key: TextureKey.Ground, type: 'svg', file: 'images/ground.svg', width: 400, height: 120 },
  { key: TextureKey.Coin, type: 'svg', file: 'images/coin.svg', width: 90, height: 90 },
  { key: TextureKey.CoinSpin, type: 'svg', file: 'images/coin-spin.svg', width: 450, height: 90, frames: 5 },
  { key: TextureKey.CoinBonus, type: 'svg', file: 'images/coin-bonus.svg', width: 90, height: 90 },
  { key: TextureKey.CoinBonusSpin, type: 'svg', file: 'images/coin-bonus-spin.svg', width: 450, height: 90, frames: 5 },
  { key: TextureKey.CoinStarter, type: 'svg', file: 'images/coin-starter.svg', width: 90, height: 90 },
  { key: TextureKey.CoinBaby, type: 'svg', file: 'images/coin-baby.svg', width: 90, height: 90 },
  { key: TextureKey.Plane, type: 'svg', file: 'images/plane.svg', width: 192, height: 80 },
  { key: TextureKey.Cloud, type: 'svg', file: 'images/cloud.svg', width: 150, height: 91 },
];

/** City skyline of a location (`koeln`, `berlin`); Cologne for anything else. */
export function skylineFor(location: string | null | undefined): TextureKey {
  return location === 'berlin' ? TextureKey.SkylineBerlin : TextureKey.Skyline;
}

/** Side of the obstacle columns' dark side face: as in the artwork (left), or mirrored (right). */
export type PipeSide = 'left' | 'right';

/** Dark side face of the columns like on the mural of a location: Berlin right, Cologne (and anything else) left. */
export function pipeSideFor(location: string | null | undefined): PipeSide {
  return location === 'berlin' ? 'right' : 'left';
}

/** default folder (relative to the page) that holds the assets */
export const DEFAULT_ASSET_PATH = 'assets/';
